import React, { useEffect, useRef } from 'react';
import { MotionData, InteractionMode } from '../types';
import { motionService } from '../services/motionService';
import { cameraMotionService } from '../services/cameraMotionService';
import { audioEngine } from '../services/audioEngine';

interface VisualizerProps {
  color: string;
  interactionMode: InteractionMode;
  isActive: boolean;
  onStageClick?: () => void;
  isPerformanceActive?: boolean;
}

export const Visualizer: React.FC<VisualizerProps> = ({ 
  color, 
  interactionMode, 
  isActive, 
  onStageClick, 
  isPerformanceActive = true
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Live motion ref driven by direct subscriptions to avoid heavy 60fps React re-renders
  const motionRef = useRef<MotionData>({
    x: 0, y: 0, z: 0, rotAlpha: 0, rotBeta: 0, rotGamma: 0, intensity: 0, jerk: 0
  });
  const colorRef = useRef<string>(color);
  const isPerfActiveRef = useRef<boolean>(isPerformanceActive);

  useEffect(() => {
    colorRef.current = color;
  }, [color]);

  useEffect(() => {
    isPerfActiveRef.current = isPerformanceActive;
  }, [isPerformanceActive]);

  // Subscribe directly to motion events for low-latency, zero-overhead canvas rendering
  useEffect(() => {
    const unsubMotion = motionService.subscribe((data) => {
      motionRef.current = data;
    });
    const unsubCamera = cameraMotionService.subscribeMotion((data) => {
      motionRef.current = data;
    });
    return () => {
      unsubMotion();
      unsubCamera();
    };
  }, []);
  
  // Persistent floating mindfulness particles
  const particlesRef = useRef(
    Array.from({ length: 35 }, () => ({
      x: Math.random(),
      y: Math.random(),
      radius: 1 + Math.random() * 2.5,
      speedX: (Math.random() - 0.5) * 0.0008,
      speedY: -0.0005 - Math.random() * 0.001,
      opacity: 0.15 + Math.random() * 0.35,
      hueOffset: (Math.random() - 0.5) * 20
    }))
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isActive) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    let animationFrameId: number;
    let time = 0;
    const particles = particlesRef.current;

    const render = () => {
      const currentMotion = motionRef.current;
      const currentColor = colorRef.current;
      const perfActive = isPerfActiveRef.current;

      const effectiveIntensity = perfActive ? (currentMotion.intensity || 0) : 0;
      time += 0.02 + (effectiveIntensity * 0.05);
      
      const { width, height } = canvas.getBoundingClientRect();
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }

      // Deep, soothing neutral background with subtle gradient
      const bgGrad = ctx.createRadialGradient(width / 2, height / 2, 20, width / 2, height / 2, Math.max(width, height) * 0.8);
      bgGrad.addColorStop(0, '#111827');
      bgGrad.addColorStop(1, '#030712');
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, width, height);
      
      const cx = width / 2;
      const cy = height / 2;

      // Draw floating mindful particles
      particles.forEach((p) => {
        p.x += p.speedX * (1 + effectiveIntensity * 2);
        p.y += p.speedY * (1 + effectiveIntensity * 2);
        if (p.y < 0) { p.y = 1; p.x = Math.random(); }
        if (p.x < 0) p.x = 1;
        if (p.x > 1) p.x = 0;

        const px = p.x * width;
        const py = p.y * height;
        ctx.beginPath();
        ctx.arc(px, py, p.radius * (1 + effectiveIntensity * 0.8), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${p.opacity * (0.4 + effectiveIntensity * 0.6)})`;
        ctx.fill();
      });
      
      // Stillness vs Flow Dynamics:
      // When still, a gentle 4-6 bpm breathing wave pulses effortlessly.
      const breathCycle = (Math.sin(time * 0.8) + 1) * 0.5; // 0 to 1 smooth breath
      const baseRadius = Math.min(width, height) * (0.2 + (breathCycle * 0.05));
      const kineticPulse = effectiveIntensity * 35;
      const radius = baseRadius + kineticPulse;

      // Outer Grounding Halo
      const outerGlow = ctx.createRadialGradient(cx, cy, radius * 0.5, cx, cy, radius * 2.2);
      outerGlow.addColorStop(0, `${currentColor}25`);
      outerGlow.addColorStop(0.6, `${currentColor}08`);
      outerGlow.addColorStop(1, 'transparent');
      ctx.fillStyle = outerGlow;
      ctx.beginPath();
      ctx.arc(cx, cy, radius * 2.2, 0, Math.PI * 2);
      ctx.fill();

      // Fluid Kinetic Somatic Mesh
      ctx.beginPath();
      const points = 120;
      for (let i = 0; i <= points; i++) {
        const angle = (i / points) * Math.PI * 2;
        const deformX = Math.cos(angle * 3 + time * 1.5) * (perfActive ? (currentMotion.rotBeta || 0) * 0.6 : 0);
        const deformY = Math.sin(angle * 2 + time * 1.5) * (perfActive ? (currentMotion.rotGamma || 0) * 0.6 : 0);
        const noise = (Math.sin(angle * 8 + time * 3)) * effectiveIntensity * (currentMotion.jerk || 1) * 8;
        
        const r = radius + deformX + deformY + noise;
        const x = cx + Math.cos(angle) * r;
        const y = cy + Math.sin(angle) * r;
        
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      
      ctx.closePath();
      
      const meshGrad = ctx.createRadialGradient(cx, cy, radius * 0.2, cx, cy, radius * 1.3);
      meshGrad.addColorStop(0, currentColor);
      meshGrad.addColorStop(0.8, `${currentColor}99`);
      meshGrad.addColorStop(1, `${currentColor}33`);
      
      ctx.fillStyle = perfActive ? meshGrad : 'rgba(255,255,255,0.06)';
      ctx.shadowBlur = perfActive ? 25 + (effectiveIntensity * 30) : 10;
      ctx.shadowColor = currentColor;
      ctx.fill();
      ctx.shadowBlur = 0;

      // Subtle Inner Core (Singing bowl center)
      ctx.beginPath();
      ctx.arc(cx, cy, radius * 0.35 * (0.85 + breathCycle * 0.15), 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.fill();

      animationFrameId = requestAnimationFrame(render);
    };
    
    render();
    return () => cancelAnimationFrame(animationFrameId);
  }, [isActive]);

  const handleTouch = (e: React.TouchEvent) => {
    if (e.type === 'touchstart') {
      audioEngine.resume();
    }
    if (interactionMode !== InteractionMode.TOUCH || !isActive) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    motionService.handleManualTouch(e.touches[0], rect, e.type === 'touchstart' ? 'start' : 'move');
  };

  const handleTouchEnd = () => {
    if (interactionMode !== InteractionMode.TOUCH || !isActive) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    motionService.handleManualTouch(null, rect, 'end');
  };

  return (
    <div 
      ref={containerRef}
      className="w-full h-full absolute top-0 left-0 overflow-hidden cursor-crosshair select-none"
      onTouchStart={handleTouch}
      onTouchMove={handleTouch}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onClick={onStageClick}
    >
      <canvas 
        ref={canvasRef} 
        className="w-full h-full visualizer-canvas pointer-events-none" 
      />
    </div>
  );
};
